"use client";

import { AnimatePresence, m, useReducedMotion } from "motion/react";
import { useMemo } from "react";

import { ContactSalesFormCard } from "@/app/landing-page/_components/contact-sales-form-card";
import { ContactSalesThanksCard } from "@/app/landing-page/_components/contact-sales-thanks-card";
import {
  LANDING_MOTION_DURATION,
  LANDING_MOTION_EASE,
  LANDING_MOTION_Y,
} from "@/lib/motion/landing-presets";

export type ContactSalesCardStep = "form" | "thanks";

export type ContactSalesAnimatedStepCardsProps = {
  step: ContactSalesCardStep;
  onSubmitted: () => void;
  onTryDemo?: () => void;
};

/**
 * Only the right-hand card animates on form ↔ thanks; left copy stays put.
 */
export function ContactSalesAnimatedStepCards({
  step,
  onSubmitted,
  onTryDemo,
}: ContactSalesAnimatedStepCardsProps) {
  const shouldReduceMotion = useReducedMotion();

  const cardVariants = useMemo(
    () =>
      shouldReduceMotion
        ? {
            hidden: { opacity: 1, y: 0 },
            visible: { opacity: 1, y: 0 },
            exit: { opacity: 1, y: 0 },
          }
        : {
            hidden: { opacity: 0, y: LANDING_MOTION_Y },
            visible: { opacity: 1, y: 0 },
            exit: { opacity: 0, y: -LANDING_MOTION_Y },
          },
    [shouldReduceMotion],
  );

  const cardTransition = useMemo(
    () => ({
      duration: LANDING_MOTION_DURATION,
      ease: LANDING_MOTION_EASE,
    }),
    [],
  );

  return (
    <AnimatePresence mode="wait">
      {step === "form" ? (
        <m.div
          key="contact-sales-form"
          className="shrink-0"
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          transition={cardTransition}
        >
          <ContactSalesFormCard onSubmitted={onSubmitted} />
        </m.div>
      ) : (
        <m.div
          key="contact-sales-thanks"
          className="shrink-0"
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          transition={cardTransition}
        >
          <ContactSalesThanksCard onTryDemo={onTryDemo} />
        </m.div>
      )}
    </AnimatePresence>
  );
}
